import {
  ArrowLeft,
  CalendarDays,
  Check,
  CircleCheck,
  Loader2,
  Lock,
  OctagonMinus,
  Play,
  Undo2,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { type RoadmapItem, type RoadmapStatusChange, useSetRoadmapStatus } from "@/lib/api";
import { apiErrorMessage, inlineFailure } from "@/lib/api-errors";
import { belgianDay, formatDate, formatDayMonth, formatMonth } from "@/lib/format";
import { cn } from "@/lib/utils";

import { Failure, focusFell } from "./details-parts";
import { FinishForm } from "./finish-form";
import { STATUS_ICON } from "./status-menu";
import {
  currentMonth,
  isOpenBlocker,
  lockNote,
  monthOptions,
  planMonthLabel,
  ROADMAP_STATUSES,
  type RoadmapStatus,
  statusLabel,
  type StatusMove,
  statusMoves,
} from "./utils";

/** How many months ahead an idea can be planned. */
const PLAN_MONTHS = 12;

/** The moves that take an item further. */
const FORWARD: ReadonlySet<StatusMove> = new Set(["plan", "start", "finish"]);

/** A quiet text button next to the next step: the way back, or another month. */
const QUIET_LINK =
  "text-muted hover:text-primary focus-visible:ring-ring tap-target inline-flex items-center gap-1 rounded-sm text-sm underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:outline-none aria-disabled:cursor-wait pointer-coarse:relative";

interface DetailsStageProps {
  item: RoadmapItem;
  /** The month Inplannen offers first ("2026-11"): the month on show, never a past one. */
  planMonth: string;
  pastMonth: boolean;
  /** Opens Planning's editor: "Eerst een inspanning kiezen". */
  onChooseEffort: () => void;
}

/**
 * Where the item stands, and its next step: the stepper on the left, the one
 * forward move the server allows on the right as the primary button, what it
 * still misses under it, and the way back as a quiet link. Markeer klaar asks
 * for the actual hours in place, under the stepper. Nothing moves from a past
 * month.
 */
export function DetailsStage({ item, planMonth, pastMonth, onChooseEffort }: DetailsStageProps) {
  const { t } = useTranslation();
  const status = useSetRoadmapStatus();
  const [running, setRunning] = useState<StatusMove | null>(null);
  const [finishing, setFinishing] = useState(false);
  // The failed attempt, worded at render so a language switch re-words it.
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  const region = useRef<HTMLDivElement>(null);
  const forwardButton = useRef<HTMLButtonElement>(null);
  const shownStatus = useRef(item.status);
  // Where the focus goes once Markeer klaar's form closes: back to Markeer klaar on
  // Annuleren, to the stepper once the item is done (the button then goes).
  const afterFinish = useRef<"forward" | "stage" | null>(null);

  // After a move, the button that had the focus may be gone: the focus then goes to the stepper.
  useEffect(() => {
    if (shownStatus.current === item.status) {
      return;
    }
    shownStatus.current = item.status;
    if (focusFell()) {
      region.current?.focus();
    }
  }, [item.status]);

  useEffect(() => {
    if (finishing || afterFinish.current === null) {
      return;
    }
    const target = afterFinish.current === "forward" ? forwardButton.current : region.current;
    afterFinish.current = null;
    target?.focus();
  }, [finishing]);

  const moves = pastMonth ? [] : statusMoves(item, planMonth);
  const forward = moves.find((option) => FORWARD.has(option.move)) ?? null;
  const back = moves.find((option) => !FORWARD.has(option.move)) ?? null;
  const open = item.status === "done" ? 0 : item.blockers.filter(isOpenBlocker).length;
  const lock = lockNote(item.status, item.can, pastMonth);
  const developer = Boolean(item.developer) && item.can.set_effort;

  const move = async (key: StatusMove, change: RoadmapStatusChange) => {
    if (status.isPending) {
      return;
    }
    setFailure(null);
    setRunning(key);
    try {
      await status.mutateAsync({ itemId: item.id, change });
    } catch (error) {
      setFailure(inlineFailure({ error }));
    } finally {
      setRunning(null);
    }
  };

  const forwardLabel =
    forward?.move === "plan"
      ? t("roadmap.statusMenu.plan", { month: planMonthLabel(planMonth) })
      : forward?.move === "start"
        ? t("roadmap.details.start")
        : t("roadmap.statusMenu.finish");
  const ForwardIcon =
    forward?.move === "plan" ? STATUS_ICON.planned : forward?.move === "start" ? Play : CircleCheck;

  const runForward = () => {
    if (!forward || forward.blocked || status.isPending) {
      return;
    }
    if (forward.change) {
      void move(forward.move, forward.change);
    } else {
      setFailure(null);
      setFinishing(true);
    }
  };

  // Who sets the next step when the viewer cannot: the developer, at Velta.
  const waitsOnVelta =
    !forward && !pastMonth
      ? item.status === "planned"
        ? t("roadmap.details.waitsStart")
        : item.status === "in_progress"
          ? t("roadmap.details.waitsFinish")
          : null
      : null;

  return (
    <section
      aria-label={t("roadmap.details.stage")}
      className="space-y-3 border-t pt-4"
      data-section="stage"
    >
      <div className="flex flex-col gap-4 sm:flex-row">
        <div ref={region} tabIndex={-1} className="min-w-0 flex-1 focus:outline-none">
          <Stepper item={item} />
        </div>
        <div className="flex shrink-0 flex-col gap-1.5 sm:w-60">
          {forward && !finishing && (
            <Button
              ref={forwardButton}
              size="sm"
              onClick={runForward}
              // Never `disabled` while it runs: that would drop the focus.
              aria-disabled={status.isPending || undefined}
              disabled={forward.blocked !== null}
              className="w-full aria-disabled:cursor-wait"
              data-next={forward.move}
            >
              {running === forward.move ? (
                <Loader2 aria-hidden="true" className="size-4 animate-spin" />
              ) : (
                <ForwardIcon aria-hidden="true" className="size-4" />
              )}
              {forwardLabel}
            </Button>
          )}
          {forward?.blocked && (
            <p className="text-sm" data-section="missing">
              {developer ? (
                <button
                  type="button"
                  onClick={onChooseEffort}
                  className="text-primary focus-visible:ring-ring tap-target rounded-sm text-left font-medium underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:outline-none pointer-coarse:relative"
                  data-missing="effort"
                >
                  {t("roadmap.statusMenu.needsEffort")}
                </button>
              ) : (
                <span className="text-muted">{t("roadmap.details.effortByVelta")}</span>
              )}
            </p>
          )}
          {item.status === "done" && item.done_at && (
            <p className="text-muted flex items-center gap-1.5 text-sm">
              <CircleCheck aria-hidden="true" className="text-primary size-4" />
              {t("roadmap.details.doneOn", { date: formatDate(item.done_at) })}
            </p>
          )}
          {waitsOnVelta && <p className="text-muted text-sm">{waitsOnVelta}</p>}
          {open > 0 && !finishing && (
            <p className="text-muted flex items-center gap-1.5 text-sm" data-section="open-count">
              <OctagonMinus aria-hidden="true" className="size-4 shrink-0" />
              {t("roadmap.details.openBlockers", { count: open })}
            </p>
          )}
          {!finishing && ((forward?.move === "plan" && !forward.blocked) || back) && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              {forward?.move === "plan" && !forward.blocked && (
                <PlanMonthMenu
                  planMonth={planMonth}
                  pending={status.isPending}
                  onPlan={(month) => void move("plan", { status: "planned", month })}
                />
              )}
              {back && (
                <button
                  type="button"
                  onClick={() => back.change && void move(back.move, back.change)}
                  aria-disabled={status.isPending || undefined}
                  className={QUIET_LINK}
                  data-back={back.move}
                >
                  {running === back.move ? (
                    <Loader2 aria-hidden="true" className="size-3.5 animate-spin" />
                  ) : back.move === "unplan" ? (
                    <Undo2 aria-hidden="true" className="size-3.5" />
                  ) : (
                    <ArrowLeft aria-hidden="true" className="size-3.5" />
                  )}
                  {t(back.move === "unplan" ? "roadmap.details.unplan" : "roadmap.details.unstart")}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      {finishing && (
        <div className="bg-accent-light/40 rounded-md border p-3">
          <FinishForm
            itemId={item.id}
            onCancel={() => {
              afterFinish.current = "forward";
              setFinishing(false);
            }}
            onFinished={() => {
              afterFinish.current = "stage";
              setFinishing(false);
            }}
          />
        </div>
      )}
      {lock && (
        <p className="text-muted flex items-start gap-2 text-sm">
          <Lock aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
          {t(lock)}
        </p>
      )}
      {failure && <Failure>{apiErrorMessage(failure.error, "setRoadmapStatus")}</Failure>}
    </section>
  );
}

interface PlanMonthMenuProps {
  planMonth: string;
  pending: boolean;
  onPlan: (month: string) => void;
}

/** "Andere maand", next to Inplannen: plan in another month, from this month on. */
function PlanMonthMenu({ planMonth, pending, onPlan }: PlanMonthMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const months = monthOptions(currentMonth(), PLAN_MONTHS, planMonth);
  return (
    <DropdownMenu open={open} onOpenChange={(next) => setOpen(next && !pending)}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-disabled={pending || undefined}
          className={QUIET_LINK}
          data-plan-other=""
        >
          <CalendarDays aria-hidden="true" className="size-3.5" />
          {t("roadmap.details.planOther")}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-80 min-w-52">
        <DropdownMenuLabel className="text-muted text-xs font-normal">
          {t("roadmap.details.planIn")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {months.map((month) => (
          <DropdownMenuItem key={month} onSelect={() => onPlan(month)} data-plan-month={month}>
            <Check
              aria-hidden="true"
              className={cn("text-primary", month !== planMonth && "invisible")}
            />
            <span className="first-letter:uppercase">{formatMonth(month)}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * The four stages as square blocks with 2 px gaps: those passed in the light
 * blue, the current one in De Ceuster blue, those to come in light grey. Under
 * each its name, the current one in bold, and a date where one belongs: since
 * when it is an idea, the month it is planned in, the day it was done.
 */
function Stepper({ item }: { item: RoadmapItem }) {
  const { t } = useTranslation();
  const reached = ROADMAP_STATUSES.indexOf(item.status);
  const sub = (stage: RoadmapStatus): string | null => {
    if (stage === "idea" && item.status === "idea") {
      const day = belgianDay(item.created_at);
      return day ? t("roadmap.details.ideaSince", { date: formatDayMonth(day) }) : null;
    }
    if (stage === "planned" && item.month && item.status !== "idea" && item.status !== "done") {
      return formatMonth(item.month, "short");
    }
    if (stage === "done" && item.done_at) {
      return formatDate(item.done_at);
    }
    return null;
  };
  return (
    <ol className="flex gap-0.5" data-section="stepper">
      {ROADMAP_STATUSES.map((stage, index) => {
        const Icon = STATUS_ICON[stage];
        const current = index === reached;
        const passed = index < reached;
        const note = sub(stage);
        return (
          <li
            key={stage}
            aria-current={current ? "step" : undefined}
            data-stage={stage}
            className="min-w-0 flex-1"
          >
            <span
              aria-hidden="true"
              className={cn(
                "block h-2",
                current ? "bg-primary" : passed ? "bg-highlight" : "bg-chart-3",
              )}
            />
            <div className="mt-2 flex items-start gap-1">
              <Icon
                aria-hidden="true"
                strokeWidth={1.75}
                className={cn(
                  "mt-0.5 hidden size-4 shrink-0 sm:inline",
                  current || passed ? "text-primary" : "text-muted",
                )}
              />
              <div className="min-w-0">
                <span
                  className={cn(
                    "block truncate text-sm",
                    current ? "text-foreground font-semibold" : "text-muted",
                  )}
                >
                  {statusLabel(stage)}
                  {passed && <span className="sr-only"> {t("roadmap.details.passed")}</span>}
                </span>
                {/* Always a line, so a stage without a date never moves the rest. */}
                <span className="text-muted block h-4 truncate text-xs tabular-nums">{note}</span>
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
